import { Card } from "@components/cards/card";
import { Pagination } from "@components/pagination";
import { RaffleDefaultTickets } from "@components/raffle-tickets/default";
import { useAuthContext } from "@hooks/contexts/auth-context";
import {
  IRaffleDefaultDTO,
  IRaffleDefaultTicketDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { IRaffleTicketFilter } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { RaffleStatusEnum } from "@hooks/enums/raffle";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import { FiMinus, FiPlus, FiRefreshCcw } from "react-icons/fi";
import { FaTicketAlt } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import { useLocation } from "react-router-dom";

interface IProps {
  raffle: IRaffleDefaultDTO;
  raffleKey: string;
}

export const RaffleTicketVisitor = ({ raffle, raffleKey }: IProps) => {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const { client } = useAuthContext();
  const { state } = useLocation();
  const [selectedTickets, setSelectedTickets] = useState<string[]>(
    state?.raffleKey === raffleKey ? state?.selectedTickets || [] : [],
  );
  const [page, setPage] = useState(1);
  const [quantity, setQuantity] = useState(15);
  const [order] = useState("ASC");
  const [status, setStatus] = useState<IRaffleTicketFilter | undefined>();
  const [totalPages, setTotalPages] = useState(0);
  const [tickets, setTickets] = useState<IRaffleDefaultTicketDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      quantity: String(quantity),
      campaign: raffle.campaign,
      key: raffleKey,
      order,
    });
    if (status) params.set("status", status);
    if (status === "m") params.set("myTickets", "true");
    api
      .get(`/raffleClient/default/tickets?${params}`)
      .then(({ data }) => {
        if (!active) return;
        setTickets(data.ticketData || []);
        setTotalPages(Math.ceil((data.quantity || 0) / quantity));
      })
      .catch((error) => {
        if (active)
          pushToastify({
            message:
              error?.response?.data?.message ||
              "Falha ao carregar os bilhetes.",
            type: "error",
            theme: "dark",
          });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    page,
    quantity,
    order,
    status,
    raffle.campaign,
    raffleKey,
    refresh,
    pushToastify,
  ]);

  const changeQuantity = (delta: number) => {
    setPage(1);
    setQuantity((current) =>
      Math.max(
        10,
        Math.min(Math.max(10, Math.min(raffle.quantity, 50)), current + delta),
      ),
    );
  };

  return (
    <Card className="visit-panel visit-tickets">
      <div className="visit-section-heading">
        <div className="visit-heading-icon">
          <FaTicketAlt />
        </div>
        <div>
          <h2>Escolha seus bilhetes</h2>
          <p>Selecione um ou mais bilhetes para participar da campanha.</p>
        </div>
      </div>
      <div className="visit-ticket-controls">
        <label className="visit-control">
          Exibir
          <span className="visit-stepper">
            <button
              type="button"
              onClick={() => changeQuantity(-5)}
              disabled={loading || quantity <= 10}
              title="Mostrar menos bilhetes"
            >
              <FiMinus />
            </button>
            <strong>{quantity}</strong>
            <button
              type="button"
              onClick={() => changeQuantity(5)}
              disabled={loading || quantity >= Math.min(raffle.quantity, 50)}
              title="Mostrar mais bilhetes"
            >
              <FiPlus />
            </button>
          </span>
        </label>
        <label className="visit-control">
          Status
          <select
            className="form-select"
            aria-label="Filtrar bilhetes por status"
            value={status || ""}
            disabled={loading}
            onChange={(event) => {
              setPage(1);
              setStatus(
                (event.target.value as IRaffleTicketFilter) || undefined,
              );
            }}
          >
            <option value="">Todos</option>
            <option value="a">Disponíveis</option>
            <option value="u">Indisponíveis</option>
            {client && <option value="m">Meus bilhetes</option>}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-outline-primary visit-refresh"
          title="Atualizar bilhetes"
          aria-label="Atualizar bilhetes"
          disabled={loading}
          onClick={() => setRefresh((value) => value + 1)}
        >
          <FiRefreshCcw />
        </button>
      </div>
      {loading ? (
        <Skeleton count={3} height={66} />
      ) : (
        !tickets.length && (
          <p className="text-center visit-muted py-4 mb-0">
            Nenhum bilhete encontrado.
          </p>
        )
      )}
      <RaffleDefaultTickets
        raffle={raffle}
        raffleKey={raffleKey}
        tickets={loading ? [] : tickets}
        ticketsForRow={5}
        selectedTickets={selectedTickets}
        onSelectedTicketsChange={setSelectedTickets}
        canSelectTickets={raffle.status === RaffleStatusEnum.ACTIVE}
      />
      {totalPages > 1 && (
        <div className="visit-pagination">
          <span>
            Página {page} de {totalPages}
          </span>
          <Pagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            maxPageShow={5}
          />
        </div>
      )}
    </Card>
  );
};
